/* Partner Carrier Portal pages (Phase PP-4 · Feb 2026).
   2 page exports (Overview + Shipments table with multi-action drawer).
   Theme: amber.
*/
import { useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import {
  Truck, Activity, RefreshCw, MapPin, Receipt, AlertTriangle, Package,
  Check, CircleCheck, Send, FileText,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../../components/ui/dialog";

function KpiTile({ label, value, sub, icon: Icon, tone = "slate", testid }) {
  return (
    <div data-testid={testid} className={`rounded-xl border bg-${tone}-50 border-${tone}-200 px-4 py-3`}>
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
        {Icon && <Icon className={`w-4 h-4 text-${tone}-500`} />}
      </div>
      <div className="text-2xl font-semibold tabular-nums text-slate-900 mt-1.5">{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

const fmtMoney = n => n == null ? "—" : new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 }).format(n);
const fmtTime = s => { try { return new Date(s).toLocaleDateString(); } catch { return s || "—"; } };

const STATUS_TONE = {
  delivered:  "emerald",
  in_transit: "sky",
  booked:     "violet",
  exception:  "rose",
  cancelled:  "slate",
};

// ════════════════════════════════════════════════════════════════════════
// OVERVIEW
// ════════════════════════════════════════════════════════════════════════
export function CarrierOverview() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const load = async () => {
    const { data } = await api.get("/partner/carrier/overview", { params: { since_days: days } });
    setData(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);

  return (
    <div data-testid="carrier-overview" className="space-y-5">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="display text-3xl tracking-tight flex items-center gap-2.5">
            <Truck className="w-6 h-6 text-amber-500"/> Carrier Overview
          </h1>
          <p className="text-sm text-slate-600 mt-1">Assigned shipments + on-time % + revenue + exceptions.</p>
        </div>
        <div className="flex gap-2">
          <Select value={String(days)} onValueChange={v => setDays(Number(v))}>
            <SelectTrigger className="w-32 h-9" data-testid="carrier-window"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="7">7 days</SelectItem>
              <SelectItem value="30">30 days</SelectItem>
              <SelectItem value="90">90 days</SelectItem>
              <SelectItem value="365">1 year</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={load} data-testid="carrier-refresh"><RefreshCw className="w-4 h-4 mr-1.5"/> Refresh</Button>
        </div>
      </header>

      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="carrier-kpis">
            <KpiTile testid="carrier-kpi-total"     label="Total"      value={data.shipments.total}      icon={Package}   tone="slate" />
            <KpiTile testid="carrier-kpi-booked"    label="Booked"     value={data.shipments.booked}     icon={Activity}  tone="violet" />
            <KpiTile testid="carrier-kpi-transit"   label="In transit" value={data.shipments.in_transit} icon={Truck}     tone="sky" />
            <KpiTile testid="carrier-kpi-delivered" label="Delivered"  value={data.shipments.delivered}  icon={CircleCheck} tone="emerald" />
            <KpiTile testid="carrier-kpi-exception" label="Exceptions" value={data.shipments.exception}  icon={AlertTriangle} tone="rose" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <KpiTile testid="carrier-revenue-cost"   label="Carrier cost"   value={fmtMoney(data.revenue.carrier_cost_usd)} icon={Receipt} tone="slate" />
            <KpiTile testid="carrier-revenue-billed" label="Buyer billed"   value={fmtMoney(data.revenue.buyer_billed_usd)} icon={Receipt} tone="emerald" />
            <KpiTile testid="carrier-on-time"        label="On-time"        value={`${data.on_time_pct}%`} sub="delivered / total" icon={CircleCheck} tone={data.on_time_pct > 80 ? "emerald" : "amber"} />
          </div>
        </>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// SHIPMENTS
// ════════════════════════════════════════════════════════════════════════
export function CarrierShipments() {
  const [rows, setRows] = useState([]);
  const [byStatus, setByStatus] = useState({});
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);

  const load = async () => {
    const params = { since_days: 365 };
    if (status !== "all") params.status = status;
    if (q.trim()) params.q = q.trim();
    const { data } = await api.get("/partner/carrier/shipments", { params });
    setRows(data.rows); setByStatus(data.by_status || {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  return (
    <div data-testid="carrier-shipments" className="space-y-4">
      <h1 className="display text-2xl tracking-tight flex items-center gap-2"><Package className="w-5 h-5"/> Shipments</h1>
      <div className="grid grid-cols-5 gap-3" data-testid="ship-status-strip">
        {["booked","in_transit","delivered","exception","cancelled"].map(s => (
          <KpiTile key={s} testid={`ship-strip-${s}`} label={s} value={byStatus[s] || 0} tone={STATUS_TONE[s] || "slate"} />
        ))}
      </div>

      <div className="flex gap-2 items-center rounded-2xl border bg-white px-4 py-3">
        <Input data-testid="ship-search" placeholder="Search tracking / origin / destination" value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === "Enter" && load()} className="w-64 h-9" />
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-36 h-9" data-testid="ship-filter-status"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {Object.keys(STATUS_TONE).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={load} data-testid="ship-refresh"><RefreshCw className="w-4 h-4 mr-1.5"/> Refresh</Button>
      </div>

      <div className="rounded-2xl border bg-white overflow-hidden">
        <div className="grid grid-cols-12 px-4 py-2 bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500 border-b">
          <div className="col-span-2">Tracking</div>
          <div className="col-span-3">Route</div>
          <div className="col-span-2">Cost / Billed</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-2">Created</div>
          <div className="col-span-1 text-right">Action</div>
        </div>
        <div data-testid="ship-table">
          {rows.length === 0 && <div className="text-center text-sm text-slate-500 py-10" data-testid="ship-empty">No shipments assigned.</div>}
          {rows.map(r => (
            <div key={r.id} className="grid grid-cols-12 px-4 py-2.5 border-b text-sm items-center" data-testid={`ship-row-${r.id}`}>
              <div className="col-span-2 font-mono text-xs truncate">{r.tracking || "—"}</div>
              <div className="col-span-3 text-xs text-slate-600 truncate flex items-center gap-1.5">
                <MapPin className="w-3.5 h-3.5 text-slate-400 shrink-0"/>
                <span className="truncate">{r.origin} → {r.destination}</span>
              </div>
              <div className="col-span-2 text-xs tabular-nums">{fmtMoney(r.cost)} / {fmtMoney(r.billed)}</div>
              <div className="col-span-2"><span className={`text-[10px] px-2 py-0.5 rounded-full bg-${STATUS_TONE[r.status] || "slate"}-50 text-${STATUS_TONE[r.status] || "slate"}-700 border border-${STATUS_TONE[r.status] || "slate"}-200`}>{r.status}</span></div>
              <div className="col-span-2 text-xs text-slate-500">{fmtTime(r.created_at)}</div>
              <div className="col-span-1 text-right">
                <Button size="sm" variant="outline" onClick={() => setOpen(r)} data-testid={`ship-manage-${r.id}`}><FileText className="w-3.5 h-3.5"/></Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <ShipmentDrawer shipment={open} onClose={() => setOpen(null)} onChanged={load}/>
    </div>
  );
}

function ShipmentDrawer({ shipment, onClose, onChanged }) {
  const [detail, setDetail] = useState(null);
  const [action, setAction] = useState(null);   // 'pickup'|'pod'|'exception'|'invoice'

  useEffect(() => {
    if (!shipment) { setDetail(null); return; }
    api.get(`/partner/carrier/shipments/${shipment.id}`).then(r => setDetail(r.data));
  }, [shipment?.id]);

  if (!shipment) return null;
  const status = detail?.shipment?.status || shipment.status;
  return (
    <Dialog open={!!shipment} onOpenChange={onClose}>
      <DialogContent data-testid="ship-drawer" className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Shipment · {shipment.tracking || shipment.id.slice(0,8) + "…"}</DialogTitle>
          <DialogDescription>{shipment.origin} → {shipment.destination}</DialogDescription>
        </DialogHeader>
        {detail && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="rounded-lg border bg-slate-50 px-3 py-2"><div className="text-[10px] uppercase text-slate-500">Status</div><Badge>{status}</Badge></div>
              <div className="rounded-lg border bg-slate-50 px-3 py-2"><div className="text-[10px] uppercase text-slate-500">Carrier</div>{detail.shipment.carrier}</div>
              <div className="rounded-lg border bg-slate-50 px-3 py-2"><div className="text-[10px] uppercase text-slate-500">Cost</div>{fmtMoney(detail.shipment.cost)}</div>
              <div className="rounded-lg border bg-slate-50 px-3 py-2"><div className="text-[10px] uppercase text-slate-500">Billed to buyer</div>{fmtMoney(detail.shipment.billed)}</div>
              {detail.shipment.picked_up_at && <div className="rounded-lg border bg-emerald-50 border-emerald-200 px-3 py-2"><div className="text-[10px] uppercase text-emerald-700">Picked up</div>{new Date(detail.shipment.picked_up_at).toLocaleString()}</div>}
              {detail.shipment.delivered_at && <div className="rounded-lg border bg-emerald-50 border-emerald-200 px-3 py-2"><div className="text-[10px] uppercase text-emerald-700">Delivered</div>{new Date(detail.shipment.delivered_at).toLocaleString()}</div>}
              {detail.shipment.exception_note && <div className="rounded-lg border bg-rose-50 border-rose-200 px-3 py-2 col-span-2"><div className="text-[10px] uppercase text-rose-700">Exception</div>{detail.shipment.exception_note}</div>}
              {detail.shipment.pod_note && <div className="rounded-lg border bg-emerald-50 border-emerald-200 px-3 py-2 col-span-2"><div className="text-[10px] uppercase text-emerald-700">POD note</div>{detail.shipment.pod_note}</div>}
            </div>
            {detail.order && (
              <div className="rounded-lg border bg-violet-50 border-violet-200 p-3 text-sm" data-testid="ship-drawer-order">
                <div className="text-xs font-medium text-violet-700 mb-1">Linked order · {detail.order.code}</div>
                <div className="text-xs text-slate-600">{detail.order.items_count} line items · {fmtMoney(detail.order.total)} · est. delivery {fmtTime(detail.order.est_delivery)}</div>
              </div>
            )}
          </div>
        )}
        <DialogFooter className="flex gap-2 flex-wrap">
          {status !== "delivered" && status !== "exception" && (
            <Button variant="outline" onClick={() => setAction("pickup")} data-testid="ship-action-pickup">Mark picked up</Button>
          )}
          {status !== "delivered" && (
            <Button variant="outline" onClick={() => setAction("pod")} data-testid="ship-action-pod">Upload POD</Button>
          )}
          <Button variant="outline" onClick={() => setAction("exception")} data-testid="ship-action-exception">Flag exception</Button>
          <Button variant="outline" onClick={() => setAction("invoice")} data-testid="ship-action-invoice">Submit invoice</Button>
        </DialogFooter>
        <ActionDialog kind={action} shipment={shipment} onClose={() => setAction(null)} onDone={() => { setAction(null); onChanged?.(); onClose(); }} />
      </DialogContent>
    </Dialog>
  );
}

function ActionDialog({ kind, shipment, onClose, onDone }) {
  const [form, setForm] = useState({});
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setForm({}); setReason(""); }, [kind, shipment?.id]);

  if (!kind || !shipment) return null;

  const submit = async () => {
    if (reason.trim().length < 3) return;
    setBusy(true);
    try {
      const path = {
        pickup: `/partner/carrier/shipments/${shipment.id}/pickup`,
        pod: `/partner/carrier/shipments/${shipment.id}/pod`,
        exception: `/partner/carrier/shipments/${shipment.id}/exception`,
        invoice: `/partner/carrier/shipments/${shipment.id}/carrier-invoice`,
      }[kind];
      const body = { reason, ...form };
      if (kind === "invoice" && body.invoiced_amount_usd != null) body.invoiced_amount_usd = Number(body.invoiced_amount_usd);
      await api.post(path, body);
      toast.success(`Action submitted · ${kind}`);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || `${kind} failed`);
    } finally { setBusy(false); }
  };

  const titles = { pickup: "Mark picked up", pod: "Upload proof of delivery", exception: "Flag exception", invoice: "Submit carrier invoice" };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid={`action-${kind}-dialog`} className="max-w-md">
        <DialogHeader>
          <DialogTitle>{titles[kind]}</DialogTitle>
          <DialogDescription>{shipment.tracking || shipment.id.slice(0,8) + "…"}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {kind === "pickup" && (
            <div><Label>Tracking (optional)</Label><Input value={form.tracking || ""} onChange={e => setForm({...form, tracking: e.target.value})} data-testid="action-tracking"/></div>
          )}
          {kind === "pod" && (
            <div><Label>POD note</Label><Textarea value={form.pod_note || ""} onChange={e => setForm({...form, pod_note: e.target.value})} rows={3} data-testid="action-pod-note" placeholder="Signed by J. Doe at receiving dock"/></div>
          )}
          {kind === "exception" && (
            <div><Label>Exception note</Label><Textarea value={form.note || ""} onChange={e => setForm({...form, note: e.target.value})} rows={3} data-testid="action-exception-note" placeholder="Refused at receiving · damaged packaging"/></div>
          )}
          {kind === "invoice" && (
            <>
              <div><Label>Invoiced amount (USD)</Label><Input type="number" value={form.invoiced_amount_usd || ""} onChange={e => setForm({...form, invoiced_amount_usd: e.target.value})} data-testid="action-invoice-amount"/></div>
              <div><Label>Invoice number</Label><Input value={form.invoice_number || ""} onChange={e => setForm({...form, invoice_number: e.target.value})} data-testid="action-invoice-number"/></div>
            </>
          )}
          <div><Label>Audit reason</Label><Textarea value={reason} onChange={e => setReason(e.target.value)} rows={2} data-testid="action-reason"/></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="action-submit" onClick={submit} disabled={busy || reason.trim().length < 3}>{busy ? "Submitting…" : "Submit"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
